import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  VStack,
  useToast,
  Box,
  CloseButton,
  HStack,
} from '@chakra-ui/react';
import { OtpInput, SegmentedControl } from '@design-system';
import { SecurityLockIcon, UserGuestIcon, MailIcon, ExitIcon, InfoSettingsIcon, SuccessCheckIcon } from 'components/icons/SecurityIcons';
import { useLogin, useRequestOtp } from 'pages/AuthPages/pages/LoginPage/hooks';
import { useTranslation, Trans } from 'react-i18next';
import { translations } from 'translations';
import { motion, AnimatePresence } from 'framer-motion';
import { useSession } from 'store/session';
import { IDLE_TIMEOUT_OPTIONS } from 'config/constants';
import { setLastActivity, setSessionUnlockSignal } from 'helpers/sessionContext';

interface GuestLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  name: string;
  primaryColor?: string;
  featureName?: string;
  mode?: 'GUEST' | 'RECOVERY';
  onLogout?: () => void;
}

export const GuestLoginModal: React.FC<GuestLoginModalProps> = ({
  isOpen,
  onClose,
  email,
  name,
  primaryColor = '#6366f1',
  featureName = '',
  mode = 'GUEST',
  onLogout,
}) => {
  const { t } = useTranslation();
  const featureNameFallback = featureName || t('GUEST_LOGIN.DEFAULT_FEATURE', { defaultValue: 'acceder a funciones' });
  const toast = useToast();
  const { mutateAsync: requestOtp, isLoading: isRequestingOtp } = useRequestOtp();
  const { mutateAsync: login, isLoading: isLoggingIn } = useLogin();
  const { setIsV3Expired, idleTimeout, setIdleTimeout } = useSession();

  const [step, setStep] = useState<'CONFIRM' | 'OTP'>('CONFIRM');
  const [otp, setOtp] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset state on open/close
  useEffect(() => {
    if (isOpen) {
      setStep('CONFIRM');
      setOtp('');
      setIsSuccess(false);
    }
  }, [isOpen]);

  const handleSendOtp = async () => {
    try {
      await requestOtp(email);
      setStep('OTP');
      toast({
        title: t(translations.GUEST_LOGIN.CODE_SENT_TITLE, { defaultValue: 'Código enviado' }),
        description: t(translations.GUEST_LOGIN.CODE_SENT_DESCRIPTION, { email, defaultValue: 'Revisa tu correo {{email}}' }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      // Error handled by hook or global toast usually, but just in case
      console.error(error);
    }
  };

  const handleLogin = async (code: string) => {
    try {
      await login({ email, password: code, skipRedirect: true } as any);


      // Update activity to prevent immediate re-lock
      setLastActivity();

      // Trigger multi-tab unlock (safe for clients, no-op for guests)
      setSessionUnlockSignal();

      setIsSuccess(true);

      // Small delay to show the nice animation
      setTimeout(() => {
        setIsV3Expired(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error(error);
      setOtp(''); // Clear OTP on error
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      motionPreset="slideInBottom"
      closeOnOverlayClick={mode !== 'RECOVERY'}
      closeOnEsc={mode !== 'RECOVERY'}
    >
      <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />
      <ModalContent
        bg="whiteAlpha.900"
        _dark={{ bg: "gray.800" }}
        backdropFilter="blur(16px)"
        border="1px solid"
        borderColor="whiteAlpha.300"
        borderRadius="2xl"
        boxShadow="xl"
      >
        {mode !== 'RECOVERY' && (
          <Box position="absolute" right={2} top={2} zIndex={10}>
            <CloseButton onClick={onClose} />
          </Box>
        )}

        <ModalBody p={8}>
          <VStack spacing={6} align="start" w="full">

            {/* Header / Graphic */}
            <HStack spacing={4} w="full" align="center">
              <Box
                w={10}
                h={10}
                bg="purple.50"
                _dark={{ bg: 'whiteAlpha.100' }}
                borderRadius="lg"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color={primaryColor}
                flexShrink={0}
              >
                {mode === 'GUEST' ? (
                  <UserGuestIcon width="20" height="20" />
                ) : (
                  <SecurityLockIcon width="20" height="20" />
                )}
              </Box>
              <Box fontSize="2xl" fontWeight="bold" flex={1}>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-500">
                  {mode === 'GUEST'
                    ? t(translations.GUEST_LOGIN.GREETING, { name, defaultValue: 'Hola, {{name}}' })
                    : t('GUEST_LOGIN.IDLE_LOCKED_TITLE', { defaultValue: 'Sesión suspendida' })}
                </span>
              </Box>
            </HStack>

            {step === 'CONFIRM' ? (
              <>
                <Text color="gray.500" _dark={{ color: "gray.400" }} w="full" fontSize="sm">
                  {mode === 'GUEST' ? (
                    <Trans
                      i18nKey="GUEST_LOGIN.VALIDATE_IDENTITY"
                      values={{ feature: featureNameFallback }}
                      defaults="Para <strong>{{feature}}</strong> y acceder a otras funciones, valida tu identidad."
                      components={{ strong: <strong /> }}
                    />
                  ) : (
                    <Text display="block" w="full">
                      {t('GUEST_LOGIN.IDLE_LOCKED_DESCRIPTION_2', { defaultValue: 'Para retomar, valida tu identidad.' })}
                    </Text>
                  )}
                </Text>

                <Box
                  p={3}
                  bg="gray.50"
                  _dark={{ bg: "whiteAlpha.100", borderColor: "whiteAlpha.200" }}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor="gray.200"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  w="full"
                  fontSize="sm"
                >
                  <MailIcon width="16" height="16" color={primaryColor} />
                  {email}
                </Box>

                {mode === 'RECOVERY' && (
                  <VStack align="stretch" w="full" pt={4} mt={4} borderTop="1px solid" borderColor="gray.100" _dark={{ borderColor: 'whiteAlpha.100' }} spacing={4}>
                    <Box bg="purple.50" _dark={{ bg: 'whiteAlpha.50', borderColor: 'whiteAlpha.200' }} p={3} borderRadius="xl" border="1px solid" borderColor="purple.100">
                      <VStack align="stretch" spacing={3}>
                        <HStack justify="space-between">
                          <HStack spacing={2}>
                            <Box color="purple.500">
                              <InfoSettingsIcon width="14" height="14" />
                            </Box>
                            <Text fontSize="xs" fontWeight="bold" color="purple.600" _dark={{ color: 'purple.300' }} textTransform="uppercase" letterSpacing="wider">
                              {t('GUEST_LOGIN.SECURITY_SETTINGS', { defaultValue: 'Preferencia de bloqueo' })}
                            </Text>
                          </HStack>
                          <Text fontSize="10px" color="gray.400" fontStyle="italic">
                            {t('GUEST_LOGIN.IDLE_TIME_LABEL', { defaultValue: 'Tiempo de sesión' })}
                          </Text>
                        </HStack>

                        <SegmentedControl
                          size="xs"
                          layoutId="guest-timeout-active"
                          value={idleTimeout}
                          onChange={(val) => {
                            setIdleTimeout(val);
                            toast({
                              title: t(translations.SECURITY.IDLE_TIMEOUT_UPDATED, { defaultValue: 'Preferencia actualizada' }),
                              status: 'info',
                              duration: 2000,
                              isClosable: true,
                              position: 'top',
                              variant: 'subtle'
                            });
                          }}
                          options={IDLE_TIMEOUT_OPTIONS}
                        />

                        <Text fontSize="10px" color="gray.500" textAlign="center" lineHeight="shorter">
                          {t('GUEST_LOGIN.CONFIG_HINT', { defaultValue: 'Ajústalo para que se adapte a tu ritmo de trabajo y evitar bloqueos frecuentes.' })}
                        </Text>
                      </VStack>
                    </Box>
                  </VStack>
                )}

                <Button
                  w="full"
                  size="lg"
                  colorScheme="purple"
                  bg={primaryColor}
                  _hover={{ bg: primaryColor, filter: 'brightness(1.1)' }}
                  onClick={handleSendOtp}
                  isLoading={isRequestingOtp}
                  loadingText={t(translations.GUEST_LOGIN.SENDING, { defaultValue: 'Enviando...' })}
                  boxShadow="0 4px 14px 0 rgba(99, 102, 241, 0.39)"
                  mt={mode === 'RECOVERY' ? 2 : 0}
                >
                  {t(translations.GUEST_LOGIN.SEND_CODE, { defaultValue: 'Obtener código de acceso' })}
                </Button>
              </>
            ) : (
              <VStack spacing={8} w="full" py={2}>
                <VStack spacing={2} w="full">
                  <Text textAlign="center" color="gray.500" fontSize="sm">
                    {t(translations.GUEST_LOGIN.OTP_MESSAGE, { email, defaultValue: 'Ingresa el código de 6 dígitos que enviamos a' })}
                  </Text>
                  <Text fontWeight="bold" color="gray.700" _dark={{ color: 'gray.200' }} textAlign="center">
                    {email}
                  </Text>
                </VStack>

                <Box w="full" display="flex" justifyContent="center">
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    onComplete={handleLogin}
                    primaryColor={primaryColor}
                    isDisabled={isLoggingIn}
                    autoFocus
                  />
                </Box>

                <VStack spacing={4} w="full">
                  <Button
                    variant="ghost"
                    size="sm"
                    fontWeight="medium"
                    color="gray.500"
                    _hover={{ color: primaryColor, bg: 'purple.50', _dark: { bg: 'whiteAlpha.100' } }}
                    onClick={() => setStep('CONFIRM')}
                    isDisabled={isLoggingIn}
                  >
                    {t(translations.GUEST_LOGIN.RETRY, { defaultValue: '¿No recibiste el código? Intentar de nuevo' })}
                  </Button>

                  {isLoggingIn && (
                    <HStack spacing={3}>
                      <Box className="animate-spin" w={4} h={4} border="2px solid" borderColor={primaryColor} borderTopColor="transparent" borderRadius="full" />
                      <Text fontSize="sm" fontWeight="medium" color={primaryColor}>
                        {t(translations.GUEST_LOGIN.VERIFYING, { defaultValue: 'Verificando...' })}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </VStack>
            )}

            {mode === 'RECOVERY' && onLogout && (
              <Box w="full" pt={6} textAlign="center">
                <Button
                  variant="ghost"
                  size="sm"
                  colorScheme="gray"
                  onClick={onLogout}
                  fontWeight="medium"
                  fontSize="xs"
                  color="gray.400"
                  _hover={{ color: "red.400", bg: "red.50", _dark: { bg: "whiteAlpha.100" } }}
                  leftIcon={<ExitIcon width="14" height="14" />}
                >
                  {t(translations.COMMON.LOGOUT, { defaultValue: 'Cerrar sesión por completo' })}
                </Button>
              </Box>
            )}

            <AnimatePresence>
              {isSuccess && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.5 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 20,
                    borderRadius: '1rem',
                  }}
                >
                  <motion.div
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <SuccessCheckIcon
                      width="80"
                      height="80"
                      stroke={primaryColor}
                      strokeWidth="3"
                    />
                  </motion.div>
                  <Text mt={4} fontWeight="bold" color={primaryColor}>
                    {t('GUEST_LOGIN.IDLE_LOCKED_SUCCESS', { defaultValue: '¡Sesión restaurada!' })}
                  </Text>
                </motion.div>
              )}
            </AnimatePresence>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
